import { X } from "lucide-react";
import { Dialog as D } from "radix-ui";
import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export const Dialog = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;
export const DialogPortal = D.Portal;

export const DialogOverlay = forwardRef<ComponentRef<typeof D.Overlay>, ComponentPropsWithoutRef<typeof D.Overlay>>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <D.Overlay
      ref={ref}
      className={cn("fixed inset-0 z-50 bg-overlay backdrop-blur-[2px] data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out", className)}
      {...props}
    />
  );
});

const SIZES = {
  sm: "max-w-[420px]",
  md: "max-w-[560px]",
  lg: "max-w-[720px]",
  xl: "max-w-[960px]",
} as const;

export interface DialogContentProps extends ComponentPropsWithoutRef<typeof D.Content> {
  size?: keyof typeof SIZES;
  hideClose?: boolean;
}

/**
 * Centered modal. Compose with DialogHeader / DialogBody / DialogFooter:
 *   <Dialog open={open} onOpenChange={setOpen}>
 *     <DialogContent size="md">
 *       <DialogHeader title="Invite teammates" description="…" />
 *       <DialogBody>…form…</DialogBody>
 *       <DialogFooter><Button>Send invites</Button></DialogFooter>
 *     </DialogContent>
 *   </Dialog>
 */
export const DialogContent = forwardRef<ComponentRef<typeof D.Content>, DialogContentProps>(function DialogContent(
  { className, children, size = "md", hideClose, ...props },
  ref,
) {
  return (
    <D.Portal>
      <DialogOverlay />
      <D.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-panel border border-line bg-surface text-fg shadow-lg outline-none data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out",
          SIZES[size],
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <D.Close className="absolute right-3.5 top-3.5 inline-flex size-8 items-center justify-center rounded-control text-fg-muted transition-colors hover:bg-fg/[0.06] hover:text-fg" aria-label="Close">
            <X className="size-[18px]" />
          </D.Close>
        )}
      </D.Content>
    </D.Portal>
  );
});

export function DialogHeader({ title, description, icon, className, children }: { title: ReactNode; description?: ReactNode; icon?: ReactNode; className?: string; children?: ReactNode }) {
  return (
    <div className={cn("flex gap-3.5 px-6 pb-2 pt-6 pr-14", className)}>
      {icon && <div className="flex size-10 shrink-0 items-center justify-center rounded-control bg-primary-soft text-primary-ink [&_svg]:size-5">{icon}</div>}
      <div className="min-w-0 space-y-1">
        <D.Title className="font-display text-lg font-semibold tracking-tight text-fg">{title}</D.Title>
        {description ? <D.Description className="text-base text-fg-muted">{description}</D.Description> : <D.Description className="sr-only">{typeof title === "string" ? title : "Dialog"}</D.Description>}
        {children}
      </div>
    </div>
  );
}

export function DialogBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-4", className)}>{children}</div>;
}

export function DialogFooter({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("flex flex-col-reverse gap-2 border-t border-line bg-surface-2/40 px-6 py-3.5 sm:flex-row sm:items-center sm:justify-end", className)}>{children}</div>;
}
